import { Logger } from "@better-ccflare/logger";
import type { Account } from "@better-ccflare/types";
import { isForceAccountModelEnabled } from "./force-account-model";
import { stripModelRouting } from "./model-routing";
import { safeJsonParse, validateModelMappings } from "./validation";

const log = new Logger("ModelMappings");

// Inline types to avoid Bun import issues
// Types are now defined in index.ts and exported from there

// Known model family patterns for O(1) direct matching
// Pattern order: Check "fable" before "opus" before "haiku" before "sonnet" to avoid substring
// collisions in edge cases like "claude-opus-haiku-test" (though we would never see this pattern from the client)
export const KNOWN_PATTERNS = ["fable", "opus", "haiku", "sonnet"] as const;

/**
 * Get the model family (fable/opus/sonnet/haiku) from a model ID
 * Uses the same pattern matching as mapModelName()
 * @returns Model family or null if no pattern matches
 */
export function getModelFamily(
	modelId: string,
): "fable" | "opus" | "sonnet" | "haiku" | null {
	const normalized = modelId.toLowerCase();
	for (const pattern of KNOWN_PATTERNS) {
		if (normalized.includes(pattern)) {
			return pattern;
		}
	}
	return null;
}

/**
 * Internal window key for a per-model weekly (weekly_scoped) limit, derived from
 * the model display name. Kept byte-identical between the dashboard usage rows
 * and the proxy throttle snapshots so the two match (throttle-window match /
 * amber highlight). Only needs to start with `seven_day_` (so the pace marker
 * treats it as a 7-day window) and be stable per model.
 */
export function weeklyScopedWindowKey(displayName: string): string {
	const slug = displayName
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");
	return `seven_day_${slug}`;
}

/**
 * Validate if a model ID is a valid Claude model
 * Accepts any model containing fable, opus, sonnet, or haiku (case-insensitive)
 * @returns true if model matches a known pattern
 */
export function isValidClaudeModel(modelId: string): boolean {
	return getModelFamily(modelId) !== null;
}

/**
 * Get a user-friendly error message listing allowed model patterns
 * @returns Error message string for API responses
 */
export function getAllowedModelsMessage(): string {
	return "Model must contain one of: fable, opus, sonnet, haiku (e.g., claude-fable-5, claude-opus-4-6, claude-sonnet-4-5-20250929)";
}

/**
 * Parse custom endpoint data from account's custom_endpoint field
 */
export function parseCustomEndpointData(
	customEndpoint: string | null,
): { endpoint?: string; modelMappings?: Record<string, string> } | null {
	if (!customEndpoint) {
		return null;
	}

	const trimmed = customEndpoint.trim();
	if (!trimmed.startsWith("{")) {
		// Return plain string as endpoint
		return { endpoint: trimmed };
	}

	try {
		return safeJsonParse<{
			endpoint?: string;
			modelMappings?: Record<string, string>;
		}>(trimmed, "custom_endpoint");
	} catch (error) {
		log.warn(
			`Failed to parse custom_endpoint JSON, treating as plain string: ${error instanceof Error ? error.message : String(error)}`,
		);
		return { endpoint: trimmed };
	}
}

/**
 * Parse model mappings from account's model_mappings field.
 * Values may be a single string or an ordered array of model names to try.
 */
export function parseModelMappings(
	modelMappings: string | null,
): Record<string, string | string[]> | null {
	if (!modelMappings) {
		return null;
	}

	try {
		return safeJsonParse<Record<string, string | string[]>>(
			modelMappings,
			"model_mappings",
		);
	} catch (error) {
		log.warn(
			`Failed to parse model_mappings JSON: ${error instanceof Error ? error.message : String(error)}`,
		);
		return null;
	}
}

/**
 * Normalise a model mapping value to an array.
 */
function toArray(value: string | string[]): string[] {
	return Array.isArray(value) ? value : [value];
}

/**
 * Get effective model mappings for an account, merging model_fallbacks into
 * the arrays so that model_fallbacks becomes the second+ entry for each family.
 */
export function getModelMappings(
	account: Account,
): Record<string, string | string[]> {
	const mappings: Record<string, string | string[]> = {};

	// Check for environment variable overrides (only in Node.js)
	if (
		typeof process !== "undefined" &&
		process.env?.OPENAI_COMPATIBLE_MODEL_MAPPINGS
	) {
		try {
			const envMappings = safeJsonParse<Record<string, string | string[]>>(
				process.env.OPENAI_COMPATIBLE_MODEL_MAPPINGS,
				"OPENAI_COMPATIBLE_MODEL_MAPPINGS environment variable",
			);
			Object.assign(mappings, envMappings);
		} catch (error) {
			log.warn(
				"Failed to parse OPENAI_COMPATIBLE_MODEL_MAPPINGS environment variable:",
				error,
			);
		}
	}

	// Check for account-specific mappings in model_mappings field
	const accountMappings = parseModelMappings(account.model_mappings);
	if (accountMappings) {
		Object.assign(mappings, accountMappings);
	}

	// Check for legacy mappings in custom_endpoint JSON payload (fallback)
	const customEndpointData = parseCustomEndpointData(account.custom_endpoint);
	if (customEndpointData?.modelMappings) {
		log.warn(
			`Found model mappings in custom_endpoint for account ${account.name} - this is deprecated. Use model_mappings field instead.`,
		);
		Object.assign(mappings, customEndpointData.modelMappings);
	}

	// Merge model_fallbacks into the arrays so they become the next models to try
	// after the primary mapping is exhausted. model_fallbacks is now deprecated as
	// a separate concept — the array in model_mappings supersedes it.
	if (account.model_fallbacks) {
		const fallbacks = parseModelFallbacks(account.model_fallbacks);
		if (fallbacks) {
			for (const [family, fallbackModel] of Object.entries(fallbacks)) {
				const existing = mappings[family];
				if (existing !== undefined) {
					const arr = toArray(existing);
					if (!arr.includes(fallbackModel)) {
						mappings[family] = [...arr, fallbackModel];
					}
				} else {
					mappings[family] = fallbackModel;
				}
			}
		}
	}

	return mappings;
}

/**
 * Check whether an account has any model mapping configuration.
 * Returns false if the account should just forward the model name unchanged.
 */
function hasAccountModelMappings(account: Account): boolean {
	if (account.model_mappings) return true;
	if (account.model_fallbacks) return true;

	const customEndpointData = parseCustomEndpointData(account.custom_endpoint);
	if (customEndpointData?.modelMappings) return true;

	// Check env override
	if (
		typeof process !== "undefined" &&
		process.env?.OPENAI_COMPATIBLE_MODEL_MAPPINGS
	) {
		try {
			const envMappings = safeJsonParse<Record<string, string | string[]>>(
				process.env.OPENAI_COMPATIBLE_MODEL_MAPPINGS,
				"OPENAI_COMPATIBLE_MODEL_MAPPINGS environment variable",
			);
			if (envMappings && Object.keys(envMappings).length > 0) return true;
		} catch {
			// Ignore — treat parse error as no env override
		}
	}

	return false;
}

/**
 * Get the ordered list of models to try for a given Anthropic model name.
 * Returns [primaryModel, ...fallbacks] from the account's model_mappings.
 * Returns null if the account has no model mapping configuration — the model
 * name should be forwarded unchanged to the upstream provider.
 */
export function getModelList(
	anthropicModel: string,
	account: Account,
): string[] | null {
	// No custom mappings configured — don't touch the model name
	if (!hasAccountModelMappings(account)) {
		return null;
	}

	const mappings = getModelMappings(account);

	// Exact match first
	if (mappings[anthropicModel] !== undefined) {
		return toArray(mappings[anthropicModel]);
	}

	// Family match
	const family = getModelFamily(anthropicModel);
	if (family && mappings[family] !== undefined) {
		return toArray(mappings[family]);
	}

	// No mapping for this model — pass through unchanged
	return [anthropicModel];
}

/**
 * Providers whose upstream natively accepts Claude model ids.
 *
 * Two features need the same answer and must not drift: a combo slot may only
 * be left empty (passthrough) for these providers, and "force account model"
 * may only route a Claude model id to these. On any other provider the name
 * would arrive at an upstream that does not know it.
 */
const PROVIDERS_ACCEPTING_CLIENT_MODEL = new Set([
	"anthropic",
	"claude-console-api",
]);

/** Whether this provider understands a Claude model id without translation. */
export function providerAcceptsClientModel(
	provider: string | null | undefined,
): boolean {
	return PROVIDERS_ACCEPTING_CLIENT_MODEL.has(provider ?? "anthropic");
}

/**
 * Providers whose upstream accepts a `provider` routing object in the body.
 *
 * The routing suffix on a model string (`model {"only":[...]}`) is only
 * meaningful to these; everywhere else it is stripped and dropped. Kept next to
 * providerAcceptsClientModel for the same reason: one answer, one place, so the
 * parser and the request rewriter cannot drift.
 */
const PROVIDERS_SUPPORTING_PROVIDER_ROUTING = new Set([
	"openrouter",
	"nanogpt",
]);

/** Whether this provider understands a `provider` routing object. */
export function providerSupportsProviderRouting(
	provider: string | null | undefined,
): boolean {
	return PROVIDERS_SUPPORTING_PROVIDER_ROUTING.has(
		(provider ?? "").trim().toLowerCase(),
	);
}

/**
 * Map Anthropic model name to provider-specific model name (first in list).
 * Optimized for known model patterns with direct matching (O(1) vs O(n log n))
 */
export function mapModelName(anthropicModel: string, account: Account): string {
	// A provider routing suffix (`model {"only":[...]}`) is never part of a
	// mapping key. Strip it so a suffixed name coming from a combo slot still
	// resolves, instead of silently falling through to a family default. The
	// returned mapping value keeps any suffix it has: the request rewriter
	// (providers' transformRequestBodyModel) reads it back out.
	const lookupModel = stripModelRouting(anthropicModel);

	// With "force account model" on, no mapping may rename the request: the
	// operator asked for this model by name, and an account that cannot serve
	// it was already excluded during selection. Guarding the single place every
	// provider funnels through is what makes that promise hold for all of them.
	if (isForceAccountModelEnabled()) return lookupModel;

	const list = getModelList(lookupModel, account);
	if (!list) return lookupModel;

	const mapped = list[0];

	if (
		process.env.DEBUG?.includes("model") ||
		process.env.DEBUG === "true" ||
		process.env.NODE_ENV === "development"
	) {
		log.info(`Model mapping: ${lookupModel} -> ${mapped}`);
	}

	return mapped;
}

/**
 * Get endpoint URL from account, falling back to default
 */
export function getEndpointUrl(account: Account): string {
	const defaultEndpoint = "https://api.openai.com";
	const customEndpointData = parseCustomEndpointData(account.custom_endpoint);

	if (customEndpointData?.endpoint) {
		// Use the parsed endpoint from JSON
		return customEndpointData.endpoint;
	}

	if (
		account.custom_endpoint &&
		!account.custom_endpoint.trim().startsWith("{")
	) {
		// Plain string URL
		return account.custom_endpoint.trim();
	}

	// No custom endpoint - use default
	return defaultEndpoint;
}

/**
 * Create custom endpoint data with endpoint and model mappings
 */
export function createCustomEndpointData(
	endpoint: string,
	modelMappings?: Record<string, string>,
): string {
	const data: { endpoint?: string; modelMappings?: Record<string, string> } = {
		endpoint,
	};

	if (modelMappings && Object.keys(modelMappings).length > 0) {
		data.modelMappings = modelMappings;
	}

	return JSON.stringify(data);
}

/**
 * Parse model fallbacks from account's model_fallbacks field.
 * Model fallbacks map model family names (fable/opus/sonnet/haiku) to fallback model names.
 */
export function parseModelFallbacks(
	modelFallbacks: string | null,
): Record<string, string> | null {
	if (!modelFallbacks) {
		return null;
	}

	try {
		return safeJsonParse<Record<string, string>>(
			modelFallbacks,
			"model_fallbacks",
		);
	} catch (error) {
		log.warn(
			`Failed to parse model_fallbacks JSON: ${error instanceof Error ? error.message : String(error)}`,
		);
		return null;
	}
}

/**
 * Validate model fallbacks for storage.
 * @deprecated Prefer storing fallbacks as arrays in model_mappings instead.
 */
export function validateAndSanitizeModelFallbacks(
	fallbacks: unknown,
): Record<string, string> | null {
	if (!fallbacks) {
		return null;
	}

	try {
		const result = validateModelMappings(fallbacks, "modelFallbacks");
		// model_fallbacks only ever stored single strings — cast back
		return Object.fromEntries(
			Object.entries(result).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]),
		);
	} catch (error) {
		log.warn(
			`Invalid model fallbacks: ${error instanceof Error ? error.message : String(error)}`,
		);
		return null;
	}
}

/**
 * Validate model mappings for storage. Values may be a string or string[].
 */
export function validateAndSanitizeModelMappings(
	mappings: unknown,
): Record<string, string | string[]> | null {
	if (!mappings) {
		return null;
	}

	try {
		return validateModelMappings(mappings, "modelMappings");
	} catch (error) {
		log.warn(
			`Invalid model mappings: ${error instanceof Error ? error.message : String(error)}`,
		);
		return null;
	}
}
